/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useParams } from 'react-router';

// Lib
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import {
	TApiResponseCreateSite,
	TBodyCreateSite,
} from '../types';
import {
	TApiError,
	TConversionFactor,
} from '@global-types/api/api';

// Constants
import { queryKeys } from '@config/constants/query-keys';
import { Types } from 'mongoose';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */

type TApiAddConversionFactorBody = {
	id?: Types.ObjectId;
	site?: Types.ObjectId;
	ratio?: number;
	startDate?: string;
};

type TConversionFactorBody = {
	ratio?: number;
	startDate?: string;
};

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */

// Create site
export const addSite = (
	body: TBodyCreateSite
): Promise<TApiResponseCreateSite> => {
	return apiClient.post('/sites', body);
};

// Create conversion factor
export const addConversionFactor = (
	body: TApiAddConversionFactorBody
): Promise<TConversionFactor> => {
	return apiClient.post('/conversion_factors', body);
};

/* -------------------------------------------------------------------------- */
/*                                   Hooks                                    */
/* -------------------------------------------------------------------------- */
export const useAddSite = (
	page: number,
	size: number,
	bodyConversionFactor?: TConversionFactorBody
) => {
	const { projectId } = useParams();
	const { t } = useTranslation();
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: async (
			formData: TApiAddConversionFactorBody
		) => {
			return addSite(formData);
		},

		onSuccess: async (
			response: TApiResponseCreateSite
		) => {
			const { id } = response;

			if (bodyConversionFactor) {
				const { ratio, startDate } = bodyConversionFactor;

				if (
					ratio !== undefined &&
					ratio !== 0 &&
					startDate
				)
					addConversionFactor({
						site: id,
						...bodyConversionFactor,
					});
			}

			notifications.show({
				title: t('global.success'),
				message: t(
					'sites.notifications.siteSuccessfullyAdded'
				),
				color: 'success',
			});

			if (projectId) {
				queryClient.invalidateQueries({
					queryKey: [
						'sites',
						{
							queryParams: {},
							searchParams: '',
						},
					],
				});
				queryClient.invalidateQueries({
					queryKey: [
						queryKeys.app.measurabilityProjects.list.key,
						projectId,
					],
				});
			} else {
				queryClient.invalidateQueries({
					queryKey: [
						queryKeys.app.sites.list.key,
						{ page: page, size: size },
					],
				});
			}
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'error',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
